/**
 * threadDrillHelper
 *
 * Shared "open thread" building blocks reused across two entry surfaces:
 * Discovery's topic-detail "Threads to pick up" cards + Threads-tab rows,
 * and the session-detail "Threads to pick up" section. Both drill into the
 * identical read-only thread-detail panel; only that panel's "Discuss
 * thread" primary button launches ADVICE chat — a card click never jumps
 * straight into chat.
 *
 * Star/archive/delete chrome is caller-supplied (`buildThreadDetailPayload`'s
 * `config`) rather than built in here: session detail's OpenThread records
 * don't carry `starredAt`/`dismissedAt`, so that chrome is simply omitted —
 * the panel degrades gracefully instead of requiring feature parity.
 *
 * @module components/threadDrillHelper
 */

import { eventBus, escapeHtml } from '../core/utils.js';
import { iconArrowUpRight } from '../utils/icons.js';
import { renderNavCard } from './navCard.js';
import { openSessionInPanel } from './sessionDrillHelper.js';

/**
 * Cap a long string to a single-line-ish length. CSS ellipsis handles the
 * visual overflow; this caps the DOM text so a runaway question can't bloat
 * the card.
 * @param {string} text
 * @returns {string}
 */
function clampToOneLine(text) {
  const clean = String(text || '').trim();
  if (clean.length <= 90) return clean;
  return `${clean.slice(0, 89).trimEnd()}…`;
}

/**
 * User-facing title for a thread card/detail: the subject-first `headline`
 * when the thread agent produced one, else the question clamped to a single
 * line (legacy/null-headline rows never render a multi-line verbose title).
 * @param {Object} question - Open-thread record
 * @returns {string} Plain (unescaped) title text
 */
export function threadTitleText(question) {
  if (question.headline && question.headline.trim()) return question.headline.trim();
  return clampToOneLine(question.question || '');
}

/**
 * One read-only thread nav card (`.note-row` contract): title + optional
 * one-line context snippet, chevron revealed on hover. Shared by the
 * topic-detail "Threads to pick up" list and the session-detail "Threads to
 * pick up" section — visual only, each caller wires its own click delegation
 * since the two open the panel through slightly different plumbing
 * (`right-panel:drill` availability, list-state bookkeeping).
 * @param {Object} thread - Open-thread record ({id, headline?, question?, context?})
 * @returns {string} HTML string
 */
export function renderThreadNavCard(thread) {
  return renderNavCard({
    title: threadTitleText(thread) || 'Open thread',
    dataAttrs: { 'data-thread-id': String(thread.id) },
    cardClass: 'thread-card',
    titleClass: 'thread-card__title',
    snippet: thread.context || '',
    snippetClass: 'thread-card__snippet'
  });
}

/**
 * Hand a thread off to chat (ADVICE). Takes the full thread object so callers
 * with a thread not held in a list (e.g. a drilled topic thread) still work.
 * @param {Object} question - OpenThread object
 */
export function discussThread(question) {
  if (!question) return;
  eventBus.emit('navigate', {
    page: 'chat',
    mode: 'ADVICE',
    force: true,
    openThreadId: question.id,
    openThread: {
      id: question.id,
      question: question.question,
      context: question.context,
      sessionId: question.sessionId,
      observationId: question.observationId || null
    }
  });
}

/**
 * Build the right-panel payload for a single thread's detail view (content
 * element + header star/options/primary config). Pure aside from the
 * session-tag drill, which defaults to the shared `openSessionInPanel` but
 * can be overridden (Discovery routes it through its own `handleGoToSession`
 * so its archive/star bookkeeping around that call stays intact).
 *
 * @param {Object} question - Full OpenThread object. `session` ({id,
 *   endedAt, sessionNumber}) and `starredAt` are optional — the source-
 *   session metadata pills and "Session N" tag only render when `session`
 *   is present; the star button is entirely caller-supplied
 *   (`config.starButton`). Session detail's own threads omit `session`
 *   deliberately: a "Session N" tag pointing at the session you're already
 *   viewing would be circular.
 * @param {Object} [config]
 * @param {Object} [config.starButton] - Passed through verbatim; omit to hide.
 * @param {Object} [config.optionsDropdown] - Passed through verbatim; omit to hide.
 * @param {(sessionNumber: string) => void} [config.onSessionTagClick] - Defaults to `openSessionInPanel`.
 * @returns {Object} A `right-panel:open` / `right-panel:drill` payload.
 */
export function buildThreadDetailPayload(question, config = {}) {
  const contextText = question.context || question.observation?.title || '';
  const sessionNumber = question.session?.sessionNumber || '';
  const sessionDate = question.session?.endedAt
    ? new Date(question.session.endedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '';
  const exploredDate = question.addressedAt
    ? new Date(question.addressedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : '';

  // Meta tags: source session date, explored date (if any), and a clickable
  // session tag. It rides the header's meta row beside grey fact pills, so it
  // is the shared `.tag--meta-link` (components/tags.css) — the same header
  // pill the experiment detail uses. In-content links (pattern instances, the
  // Activity feed) keep the outlined `.tag--session` instead; see §6.
  const metadata = [];
  if (sessionDate) {
    metadata.push(`<span class="sd-metadata__text">${escapeHtml(sessionDate)}</span>`);
  }
  if (exploredDate) {
    metadata.push(`<span class="sd-metadata__text">Explored on ${escapeHtml(exploredDate)}</span>`);
  }
  if (sessionNumber) {
    metadata.push(
      `<span class="tag tag--meta-link" data-action="view-session" data-session-id="${escapeHtml(String(question.session?.id || ''))}" data-session-number="${escapeHtml(String(sessionNumber))}">Session ${escapeHtml(String(sessionNumber))} ${iconArrowUpRight()}</span>`
    );
  }

  const titleText = threadTitleText(question);

  const detailContent = document.createElement('div');
  detailContent.className = 'session-detail-content thread-detail-content content-width';
  detailContent.innerHTML = `
    <div class="thread-detail__header">
      <h1 class="sd-title">${escapeHtml(titleText)}</h1>
      ${metadata.length ? `<div class="sd-metadata">${metadata.join('')}</div>` : ''}
    </div>

    <div class="sd-section-header">Thread to explore</div>
    <p class="thread-detail__question">${escapeHtml(question.question)}</p>

    ${contextText ? `
      <div class="sd-takeaway sd-takeaway--callout">
        <div class="sd-takeaway__label">Context</div>
        <p class="sd-takeaway__text">${escapeHtml(contextText)}</p>
      </div>
    ` : ''}
  `;

  const onSessionTagClick = config.onSessionTagClick || openSessionInPanel;
  detailContent.addEventListener('click', (e) => {
    const sessionTag = e.target.closest('.tag--meta-link');
    if (sessionTag) {
      e.preventDefault();
      onSessionTagClick(sessionTag.dataset.sessionNumber);
    }
  });

  return {
    title: '',
    titleAlign: 'left',
    starButton: config.starButton,
    optionsDropdown: config.optionsDropdown,
    primaryButton: {
      label: 'Discuss thread',
      onClick: () => discussThread(question)
    },
    content: detailContent,
    contentType: 'discovery-thread'
  };
}
